"use client";

import { useEffect, useState } from "react";
import { onSnapshot, orderBy, query, where } from "firebase/firestore";
import { getDb } from "@/lib/firebase/client";
import { paths, refCol } from "@/lib/db/paths";
import {
  completionSchema,
  type Completion,
  type CompletionStatus,
} from "@/lib/schemas";

type State = {
  completions: Completion[];
  loading: boolean;
  error: string | null;
};

type Opts = {
  status?: CompletionStatus;
  completedBy?: string;
};

const cache = new Map<string, Completion[]>();
const cacheKey = (hid: string, opts: Opts) =>
  `${hid}|${opts.status ?? "*"}|${opts.completedBy ?? "*"}`;

export function useCompletions(
  householdId: string | undefined,
  opts: Opts = {},
): State {
  const { status, completedBy } = opts;
  const key = householdId ? cacheKey(householdId, { status, completedBy }) : "";
  const cached = key ? cache.get(key) : undefined;
  const [state, setState] = useState<State>({
    completions: cached ?? [],
    loading: cached === undefined,
    error: null,
  });

  useEffect(() => {
    if (!householdId) return;
    const db = getDb();
    const constraints = [];
    if (status) constraints.push(where("status", "==", status));
    if (completedBy)
      constraints.push(where("completedBy", "==", completedBy));
    constraints.push(orderBy("completedAt", "desc"));
    const q = query(refCol(db, paths.completions(householdId)), ...constraints);
    const k = cacheKey(householdId, { status, completedBy });
    const unsub = onSnapshot(
      q,
      (snap) => {
        const completions = snap.docs.flatMap((d) => {
          const parsed = completionSchema.safeParse({ id: d.id, ...d.data() });
          return parsed.success ? [parsed.data] : [];
        });
        cache.set(k, completions);
        setState({ completions, loading: false, error: null });
      },
      (err) => setState({ completions: [], loading: false, error: err.message }),
    );
    return () => unsub();
  }, [householdId, status, completedBy]);

  return state;
}
